import { useState, type KeyboardEvent } from 'react';
import { EyeOff, GripVertical, Lock } from 'lucide-react';
import type { SectionView, SectionsView } from '@nopain-newsletter/core/sections';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { problemCount, rowSections, slotSummary } from './sectionText';

const CONFIDENCE_LABEL = { medium: 'Check the cut', low: 'Unsure cut' } as const;

/**
 * The sections of the mail in order. A section is moved by dragging its
 * handle or with Alt + arrow up/down on its row; both only ask, the core
 * decides (the editor shows its refusal).
 */
export function SectionOutline({
  view,
  selected,
  readOnly,
  onSelect,
  onMove,
  onRestore,
}: {
  view: SectionsView;
  selected: string | null;
  readOnly: boolean;
  onSelect: (sectionId: string) => void;
  /** Asks to put the section at this position among the sections in the order. */
  onMove: (sectionId: string, toIndex: number) => void;
  onRestore: (sectionId: string) => void;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const ordered = rowSections(view.sections);
  const positionOf = (id: string) => ordered.findIndex((s) => s.id === id);

  const onKey = (event: KeyboardEvent, section: SectionView) => {
    if (readOnly || !event.altKey || section.rows === null) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    onMove(section.id, positionOf(section.id) + (event.key === 'ArrowUp' ? -1 : 1));
  };

  return (
    <nav aria-label="Sections" className="flex min-h-0 flex-col">
      <ol className="m-0 flex list-none flex-col gap-0.5 p-0">
        {view.sections.map((section) => {
          const movable = !readOnly && section.kind === 'movable';
          const problems = problemCount(section);
          const isSelected = section.id === selected;
          return (
            <li
              key={section.id}
              data-section={section.id}
              className={cn(
                'flex items-center gap-1 rounded-[10px] pr-2',
                isSelected ? 'bg-accent-soft' : 'hover:bg-bg',
                over === section.id && dragging !== section.id
                  ? 'outline outline-2 outline-accent'
                  : '',
                dragging === section.id ? 'opacity-50' : '',
              )}
              onDragOver={(event) => {
                if (dragging === null || section.rows === null) return;
                event.preventDefault();
                setOver(section.id);
              }}
              onDragLeave={() => setOver((current) => (current === section.id ? null : current))}
              onDrop={(event) => {
                event.preventDefault();
                const moved = dragging;
                setDragging(null);
                setOver(null);
                if (moved === null || moved === section.id || section.rows === null) return;
                onMove(moved, positionOf(section.id));
              }}
            >
              {movable ? (
                <span
                  draggable
                  title="Drag to move"
                  aria-hidden="true"
                  className="flex h-10 w-6 flex-shrink-0 cursor-grab items-center justify-center text-faint"
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', section.id);
                    setDragging(section.id);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setOver(null);
                  }}
                >
                  <GripVertical size={15} />
                </span>
              ) : (
                <span
                  className="flex h-10 w-6 flex-shrink-0 items-center justify-center text-faint"
                  title="Locked"
                >
                  <Lock size={13} aria-hidden="true" />
                </span>
              )}
              <button
                type="button"
                aria-current={isSelected ? 'true' : undefined}
                aria-keyshortcuts={movable ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
                className="min-w-0 flex-grow rounded-md py-2 text-left"
                onClick={() => onSelect(section.id)}
                onKeyDown={(event) => onKey(event, section)}
              >
                <span
                  className={cn(
                    'block truncate text-sm font-medium',
                    section.hidden ? 'text-faint line-through' : '',
                  )}
                >
                  {section.name}
                </span>
                <span className="block truncate text-xs text-muted">
                  {section.kind !== 'movable' ? 'Locked · ' : ''}
                  {section.hidden ? 'Hidden · ' : ''}
                  {section.duplicateOf !== null ? 'Copy · ' : ''}
                  {slotSummary(section.slots)}
                </span>
              </button>
              {section.hidden ? (
                <EyeOff size={14} aria-hidden="true" className="flex-shrink-0 text-faint" />
              ) : null}
              {section.confidence !== 'high' && section.rows !== null ? (
                <span
                  title={section.reason}
                  className="flex-shrink-0 rounded-full bg-warning-bg px-2 py-0.5 text-[11px] font-medium text-warning-text"
                >
                  {CONFIDENCE_LABEL[section.confidence]}
                </span>
              ) : null}
              {problems > 0 ? (
                <span
                  className="flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full bg-warning-bg px-1 text-[11px] font-semibold text-warning-text"
                  aria-label={`${problems} ${problems === 1 ? 'problem' : 'problems'}`}
                >
                  {problems}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>

      {view.removed.length > 0 ? (
        <div className="mt-3 border-t border-border pt-3">
          <h3 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
            Removed
          </h3>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {view.removed.map((section) => (
              <li key={section.id} className="flex items-center gap-2 pl-6">
                <span className="min-w-0 flex-grow truncate text-sm text-muted">
                  {section.name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={readOnly}
                  aria-label={`Restore ${section.name}`}
                  onClick={() => onRestore(section.id)}
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!readOnly && ordered.some((s) => s.kind === 'movable') ? (
        <p className="m-0 mt-3 text-xs text-muted">
          <kbd className="rounded border border-border bg-card px-1 font-mono">Alt</kbd>{' '}
          <kbd className="rounded border border-border bg-card px-1 font-mono">↑</kbd>{' '}
          <kbd className="rounded border border-border bg-card px-1 font-mono">↓</kbd> on a section
          moves it.
        </p>
      ) : null}
    </nav>
  );
}
